import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, UserMinus } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const keyOf = (e) => e.hc_enrollment_id || e.wix_participant_id;
const isPathways = (e) => ['pathways_module', 'pathways_to_employability'].includes(e.program_type || e.selected_course_type);

const currentCourses = (wixData) => {
  const entries = (wixData?.course_enrollments || []).filter((e) => !['removed', 'withdrawn'].includes(e.enrollment_status) && keyOf(e));
  if (entries.length) return entries.map((e) => ({ key: keyOf(e), name: e.course_name || 'Unknown course', pathways: isPathways(e) }));
  if (keyOf(wixData || {}) && wixData.wix_enrollment_status !== 'not_enrolled' && wixData.enrollment_status !== 'removed') {
    return [{ key: keyOf(wixData), name: wixData.selected_course_name || 'Unknown course', pathways: isPathways(wixData) }];
  }
  return [];
};

export const RemoveCourseButton = ({ caseId, wixData, participantName, onRemoved }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const courses = currentCourses(wixData);
  if (!courses.length) return null;
  const pickedCourses = courses.filter((c) => picked.includes(c.key));
  const pathwaysPicked = pickedCourses.some((c) => c.pathways);
  const names = [...(pathwaysPicked ? ['Pathways to Employability (all modules)'] : []), ...pickedCourses.filter((c) => !c.pathways).map((c) => c.name)];
  const pathwaysKeys = courses.filter((c) => c.pathways).map((c) => c.key);
  const toggle = (k) => {
    const group = pathwaysKeys.includes(k) ? pathwaysKeys : [k];
    setPicked((p) => (p.includes(k) ? p.filter((x) => !group.includes(x)) : [...new Set([...p, ...group])]));
  };

  const remove = async () => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/wix-program/remove-courses`, { course_keys: picked }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('myhelp_token')}` },
      });
      toast.success(`Removed from ${names.join(', ')}`);
      setConfirm(false); setOpen(false); setPicked([]);
      onRemoved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove the course');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setPicked([]); setOpen(true); }} data-testid="remove-from-course-btn">
        <UserMinus className="h-4 w-4 mr-2" />Remove from Course
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="remove-course-dialog">
          <DialogHeader>
            <DialogTitle>Remove from Course</DialogTitle>
            <DialogDescription>Choose the course(s) to remove {participantName || 'this participant'} from.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {courses.map((c) => (
              <label key={c.key} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer" data-testid={`remove-course-option-${c.key}`}>
                <Checkbox checked={picked.includes(c.key)} onCheckedChange={() => toggle(c.key)} />
                <span className="text-sm font-medium">{c.name}</span>
                {c.pathways && <span className="text-xs text-muted-foreground">(Pathways — removes the whole program)</span>}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="remove-course-cancel-btn">Cancel</Button>
            <Button variant="destructive" disabled={!picked.length} onClick={() => setConfirm(true)} data-testid="remove-course-continue-btn">Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirm} onOpenChange={(o) => !saving && setConfirm(o)}>
        <AlertDialogContent data-testid="remove-course-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {participantName || 'this participant'}?</AlertDialogTitle>
            <AlertDialogDescription data-testid="remove-course-confirm-text">
              {participantName || 'This participant'} will be removed from: <b>{names.join(', ')}</b>. The course(s) will disappear from HELPConnect and this Report Card.
            </AlertDialogDescription>
            {pathwaysPicked && (
              <p className="text-sm font-medium text-destructive" data-testid="remove-course-pathways-warning">
                Pathways to Employability works as one program. Removing a Pathways module removes {participantName || 'the participant'} from the whole Pathways to Employability program (all 8 modules).
              </p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving} data-testid="remove-course-confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(); }} disabled={saving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="remove-course-confirm-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
